/**
 * `log_level` — one analytics event per play turn. WebBridge.md §3, and
 * `reference/Rule&Template/rule_LOGLEVEL.md` for the house shape this follows.
 *
 * ❓ PLACEHOLDER — not in the decomp. The shipped game logs through ElephantSDK
 * in-process (`Assets/Scripts/Elephant/ElephantSDK/`), which a web build cannot
 * link, so the wrapper app carries the event instead. The *schema* is not the
 * port's invention — it is WebBridge.md §3 verbatim, nine required fields — but
 * everything derived below (difficulty buckets, progress, where `Fail` fires)
 * is a decision this file has to own and state.
 *
 * The split rule_LOGLEVEL §2 insists on is kept: **this file knows the wire
 * format, `main.ts` knows the game.** Nothing here reads a `GameSession`.
 *
 * ## The app drops what it cannot parse
 *
 * WebBridge.md §3: "status sai giá trị → app bỏ qua", and it does so silently.
 * A dropped event is indistinguishable from a game that never logged, which is
 * a miserable thing to debug, so `logLevel` refuses an unknown status locally
 * and warns — the failure surfaces in the console instead of vanishing.
 */

import { nativeBridge, type NativeBridge } from './bridge'

/** WebBridge.md §3 — the five the app accepts. */
export const LEVEL_STATUSES = ['Fail', 'Pass', 'Skip', 'Start', 'HeartBeat'] as const
export type LevelStatus = (typeof LEVEL_STATUSES)[number]

/**
 * The nine required fields, plus the one optional field this game can fill.
 *
 * `elo` / `movesUsed` / `score` / `wave` are deliberately absent: rule_LOGLEVEL
 * §3 says omit rather than fake, and this game keeps none of them. Boosters are
 * absent because the port has no boosters at all.
 */
export interface LevelLog {
  status: LevelStatus
  /** The **ladder step** (`displayLevel`), not the file index. See `levelId`. */
  currentLevel: number
  difficulty: LevelDifficultyName
  playTurnId: string
  /** Seconds of *active play* — see `LevelTurn.tick`. */
  duration: number
  levelProgress: number
  comeBackAfterFirstPass: boolean
  failCount: number
  playCount: number
  /**
   * The file actually opened — **always sent**, as a bare number string.
   *
   * ⚠️ The two authorities disagree here and DiceFlow wins, because it is the
   * shipped one. rule_LOGLEVEL §3 words this field as "level file's own id,
   * *when it differs from the number*", and the port used to implement that
   * literally: `undefined` while the ladder step and the file agreed. But
   * DiceFlow's own `wI`/`bo` never compare the two —
   *
   *     Yo = typeof A.levelId === "number" ? String(A.levelId) : ""
   *     bo = () => ({ …, currentLevelId: Yo || void 0, … })
   *
   * — so on the wire the house's shipped game carries this field on **every**
   * event, and a wrapper app written against that traffic has never seen it
   * absent. The port was the first sender to omit it.
   *
   * Why it still matters that the value is honest: 🔍 `GetResolvedFileIndex`
   * wraps the ladder back to file 16 past the last level, so from step 41 on
   * `currentLevel` and this field permanently disagree, and a funnel built on
   * `currentLevel` alone would stack every wrapped replay onto one number.
   *
   * The value is `String(fileIndex)` — bare, like DiceFlow's `String(levelId)`,
   * not the `Level_18` this port used to send. Half a fix would be worse than
   * none: a host that parses the id as a number would still choke, and the
   * experiment would come back a false negative.
   */
  currentLevelId?: string
}

/** ❓ Derived — see `levelDifficulty`. */
export type LevelDifficultyName = 'easy' | 'normal' | 'hard'

/**
 * 📐 Recovered from data — the quartiles of the shipped catalogue.
 *
 * rule_LOGLEVEL §4a says to re-measure rather than copy DiceFlow's cut points,
 * and the level files ship a `difficulty` field that cannot be used: it is
 * **0 on all 40 levels** (`LevelData.difficulty`, `LevelDifficulty` enum), so
 * it discriminates nothing. Measured over `web/public/data/levels/*.json`,
 * counting passengers (`QueueData[].people`), which is the same quantity
 * `npm run invariants` calls the level's size:
 *
 *     n=40  min 108  q1 432  median 558  q3 648  max 882
 *
 * Cutting at q1 and q3 gives 9 easy / 17 normal / 14 hard — 22 % / 42 % / 35 %.
 * Frozen against the level files in `tests/levelLog.test.ts`, so a change to
 * the catalogue that moves the quartiles fails rather than silently reshaping
 * every funnel.
 */
export const DIFFICULTY_CUTS = { normal: 432, hard: 648 } as const

export function levelDifficulty(passengers: number): LevelDifficultyName {
  if (passengers < DIFFICULTY_CUTS.normal) return 'easy'
  if (passengers < DIFFICULTY_CUTS.hard) return 'normal'
  return 'hard'
}

/**
 * ❓ Derived — share of the level's passengers who have left the platform.
 *
 * rule_LOGLEVEL §4b's analogue for a board game is "cleared / total". Here the
 * win condition is `AllQueuesClearedEvent`, and `npm run invariants` proves
 * seats == passengers on all 40 levels, so "every passenger gone" is exactly
 * the win — which makes this the one measure that reaches 100 precisely when
 * the level is won, rather than approximately.
 *
 * A passenger already walking to a bus counts as cleared, matching §4b's
 * "mid-animation counts": they have left both the queue and the waiting area
 * and no play of the board can put them back.
 */
export function levelProgress(remaining: number, total: number): number {
  if (total <= 0) return 0
  const done = ((total - remaining) / total) * 100
  return Math.max(0, Math.min(100, done))
}

// ---------------------------------------------------------------------------
// counters
// ---------------------------------------------------------------------------

/**
 * rule_LOGLEVEL §5 — *attempts since this level was last beaten*, not lifetime
 * totals, mirroring Unity's `LevelData.playTimes`. A Pass wipes both counts for
 * that level and raises `maxPassed`.
 *
 * Local only, its own key, deliberately **not** in the `save_user_data` payload
 * (§5: "Analytics is never worth widening the server payload for").
 */
export interface LevelStats {
  maxPassed: number
  plays: Record<string, number>
  fails: Record<string, number>
}

export const LEVEL_STATS_KEY = 'bus-traffic-loop.levelStats'

/** The slice of `Storage` used, so a test can pass a Map-backed stub. */
export interface StatsStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

export function emptyLevelStats(): LevelStats {
  return { maxPassed: 0, plays: {}, fails: {} }
}

function readCounts(value: unknown): Record<string, number> {
  const out: Record<string, number> = {}
  if (!value || typeof value !== 'object') return out
  for (const [key, count] of Object.entries(value as Record<string, unknown>)) {
    if (typeof count === 'number' && Number.isFinite(count) && count > 0) out[key] = Math.floor(count)
  }
  return out
}

export function readLevelStats(storage: StatsStorage | null, key = LEVEL_STATS_KEY): LevelStats {
  if (!storage) return emptyLevelStats()
  try {
    const raw = storage.getItem(key)
    if (!raw) return emptyLevelStats()
    const parsed = JSON.parse(raw) as Partial<LevelStats> | null
    if (!parsed || typeof parsed !== 'object') return emptyLevelStats()
    const maxPassed = parsed.maxPassed
    return {
      maxPassed: typeof maxPassed === 'number' && maxPassed > 0 ? Math.floor(maxPassed) : 0,
      plays: readCounts(parsed.plays),
      fails: readCounts(parsed.fails),
    }
  } catch {
    return emptyLevelStats()
  }
}

export function saveLevelStats(
  storage: StatsStorage | null,
  stats: LevelStats,
  key = LEVEL_STATS_KEY,
): void {
  if (!storage) return
  try {
    storage.setItem(key, JSON.stringify(stats))
  } catch {
    // A full or private-mode store costs an analytics counter, nothing else.
  }
}

// ---------------------------------------------------------------------------
// transport
// ---------------------------------------------------------------------------

/**
 * Normalise and send. rule_LOGLEVEL §3's four rules, in order: whole seconds,
 * progress clamped 0–100, an unknown status refused locally, optional fields
 * omitted rather than sent empty.
 */
export function logLevel(log: LevelLog, bridge: NativeBridge = nativeBridge): boolean {
  if (!(LEVEL_STATUSES as readonly string[]).includes(log.status)) {
    console.warn('[log_level] refused: unknown status', log.status)
    return false
  }
  const payload: Record<string, unknown> = {
    status: log.status,
    currentLevel: log.currentLevel,
    difficulty: log.difficulty,
    playTurnId: log.playTurnId,
    duration: Math.max(0, Math.floor(log.duration)),
    levelProgress: Math.max(0, Math.min(100, Math.round(log.levelProgress))),
    comeBackAfterFirstPass: log.comeBackAfterFirstPass,
    failCount: log.failCount,
    playCount: log.playCount,
  }
  if (log.currentLevelId !== undefined && log.currentLevelId !== '') {
    payload.currentLevelId = log.currentLevelId
  }
  bridge.send('log_level', payload)
  return true
}

// ---------------------------------------------------------------------------
// the turn
// ---------------------------------------------------------------------------

/** What `main.ts` knows about the level when a turn opens. */
export interface LevelTurnStart {
  /** The ladder step — `LocalSave.displayLevel`. */
  ladderStep: number
  /** The file `loadLevel` opened. Reported as `currentLevelId` when it differs. */
  fileIndex: number
  /** Passengers on the board when it finished building. Sizes and scores it. */
  passengers: number
}

/**
 * One play turn — from "board built" to "won or lost", and the clock in
 * between.
 *
 * ## Why the clock is not `Date.now() - startedAt`
 *
 * rule_LOGLEVEL §4d, and it is the single most expensive thing to get wrong
 * here: wall-clock bills backgrounded and AFK time, so a player who swipes away
 * and comes back the next morning reports an eight-hour level and the median
 * duration of the whole game moves. `tick` is fed the frame delta and only
 * accumulates while the round is genuinely live and on screen.
 *
 * The dt clamp is the caller's (`App.start` already clamps to 1/20 s) and is
 * load-bearing: a WebView halts `requestAnimationFrame` while backgrounded, so
 * the resume frame carries the whole away-gap unless it is capped.
 *
 * ## Where `Fail` fires
 *
 * §4c warns to fire on the point of no return, not on the lose event, because a
 * revive makes the two different. **This game has no revive** — the fail panel
 * offers Try Again and its X, and 🔍 `FailPresenter.OnCloseButtonClicked`
 * (`__0x213af10.c`) is the same six instructions as Try Again, so both reload.
 * The lose event *is* the point of no return, and `end('Fail')` hangs off it.
 *
 * A restart, by contrast, logs nothing: 🔍 `GamePlayUIModel.OnRestartButtonClicked`
 * publishes `LevelFailedEvent(FailReason.Restart)` and
 * `GameplayController.OnLevelFailed` (`__0x211d880.c`:83) returns on exactly
 * that reason before anything happens. §6 files the same behaviour as
 * deliberate: an abandoned turn produces no result, only a fresh `Start`.
 */
export class LevelTurn {
  private turnId: string | null = null
  private start: LevelTurnStart | null = null
  private playSeconds = 0
  private seq = 0

  constructor(
    private readonly stats: LevelStats,
    private readonly persist: (stats: LevelStats) => void,
    private readonly bridge: NativeBridge = nativeBridge,
    private readonly mintId: (n: number) => string = (n) => `turn_${Date.now()}_${n}`,
  ) {}

  /** True while a turn is open — the gate `main.ts` puts on `tick`. */
  get isOpen(): boolean {
    return this.turnId !== null
  }

  /** Seconds of active play so far. Exposed for the dev HUD and for tests. */
  get activeSeconds(): number {
    return this.playSeconds
  }

  /**
   * `Start`. Mints the turn id, bumps `plays`, and reports progress 0.
   *
   * Called from the end of the level build, not from the click that asked for
   * the level: `passengers` is measured off the board that actually exists.
   */
  begin(start: LevelTurnStart): void {
    this.start = start
    this.playSeconds = 0
    this.turnId = this.mintId(++this.seq)
    const key = String(start.ladderStep)
    this.stats.plays[key] = (this.stats.plays[key] ?? 0) + 1
    this.persist(this.stats)
    this.emit('Start', 0)
  }

  /**
   * Accumulate active play time. `live` is the caller's answer to "is the round
   * running and can the player see it?" — the four guards of §4d live there,
   * because only `main.ts` knows about the settings pause and the session.
   */
  tick(dt: number, live: boolean): void {
    if (!this.isOpen || !live || !(dt > 0)) return
    this.playSeconds += dt
  }

  /**
   * `Pass` or `Fail`, once. A second call is ignored — the fail path is reached
   * from more than one place and a doubled result would double the fail rate.
   *
   * `remaining` is passengers still on the platform; `Pass` ignores it, since
   * 100 is what a win means and the last few may still be walking.
   */
  end(status: 'Pass' | 'Fail', remaining: number): void {
    const start = this.start
    if (!this.isOpen || !start) return
    const key = String(start.ladderStep)

    // ⚠️ rule_LOGLEVEL §5's one ordering bug: read the counters BEFORE
    // mutating them, because a Pass wipes the very numbers the Pass has to
    // report. Get it backwards and every Pass reports playCount 1 — and nothing
    // on screen ever says so.
    const progress = status === 'Pass' ? 100 : levelProgress(remaining, start.passengers)
    if (status === 'Fail') this.stats.fails[key] = (this.stats.fails[key] ?? 0) + 1
    this.emit(status, progress)

    if (status === 'Pass') {
      this.stats.maxPassed = Math.max(this.stats.maxPassed, start.ladderStep)
      delete this.stats.plays[key]
      delete this.stats.fails[key]
    }
    this.persist(this.stats)
    this.turnId = null
  }

  /**
   * Drop the turn without logging anything — a restart or a level change. See
   * the class header for why that is the faithful behaviour and not a gap.
   */
  abandon(): void {
    this.turnId = null
    this.start = null
    this.playSeconds = 0
  }

  private emit(status: LevelStatus, progress: number): void {
    const start = this.start
    if (!start || !this.turnId) return
    const key = String(start.ladderStep)
    logLevel(
      {
        status,
        currentLevel: start.ladderStep,
        difficulty: levelDifficulty(start.passengers),
        playTurnId: this.turnId,
        duration: this.playSeconds,
        levelProgress: progress,
        // §5 — `<=`, not `<`: replaying the highest level already beaten is a
        // come-back too.
        comeBackAfterFirstPass: start.ladderStep <= this.stats.maxPassed,
        failCount: this.stats.fails[key] ?? 0,
        playCount: this.stats.plays[key] ?? 0,
        // Always, and bare — DiceFlow's `bo()` shape. See `LevelLog`.
        currentLevelId: String(start.fileIndex),
      },
      this.bridge,
    )
  }
}
